import Image from "next/image";
import { useMemo, useRef, useState } from "react";

import PlayButton from "@/assets/img/play.svg";
import { LinkUtils } from "@/utils/linkUtils";
import { Box, Button, CardMedia, Fade, IconButton, Tooltip } from "@mui/material";
import { ArrowSquareOut } from "@phosphor-icons/react";

import { styles } from "./styles";
import { AccordionCustomMediaPlayerProps } from "./types";
import { useDisclose } from "@/hooks/utilHooks";

import Lightbox from "yet-another-react-lightbox";
import { Fullscreen, Zoom } from "yet-another-react-lightbox/plugins";
import "yet-another-react-lightbox/styles.css";

export const AccordionCustomMediaPlayer: React.FC<AccordionCustomMediaPlayerProps> = ({ src, type }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [showControls, setShowControls] = useState(false);
  const mediaType = useMemo(() => type ?? LinkUtils.getFileTypeFromUrl({ fileName: src }), [src, type]);
  const { isOpen, onClose, onOpen } = useDisclose();

  if (!src || mediaType === "unknown") {
    return null;
  }

  const handlePlay = () => {
    if (!videoRef.current) {
      return;
    }

    if (videoRef.current.paused) {
      videoRef.current.play();
      setShowControls(true);
    } else {
      videoRef.current.pause();
      setShowControls(false);
    }
  };

  const handlePause = () => {
    setShowControls(false);
  };

  return (
    <>
      <Box css={styles.media_container}>
        {mediaType === "image" ? (
          <>
            <CardMedia component="img" src={src} css={styles.image} />

            <Tooltip title="Expandir">
              <IconButton css={styles.expand_image} onClick={onOpen}>
                <ArrowSquareOut size={24} color="#FFF" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <CardMedia component="video" src={src} controls={showControls} css={styles.video} ref={videoRef} onPause={handlePause} />
            <Fade in={!showControls}>
              <Button css={styles.play} onClick={handlePlay}>
                <Tooltip title="Reproduzir">
                  <Image src={PlayButton} alt="Executar Vídeo" css={styles.playbuttom_image} />
                </Tooltip>
              </Button>
            </Fade>
          </>
        )}
      </Box>

      <Lightbox
        open={isOpen}
        close={onClose}
        slides={[{ src, type: "image" }]}
        plugins={[Zoom, Fullscreen]}
        controller={{
          closeOnBackdropClick: true
        }}
        carousel={{
          finite: true
        }}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null
        }}
      />
    </>
  );
};
